import { OncologySubmissionApiResponse } from "../../../api/bigQuery/oncologySubmissionOrders";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { logError } from "../../../utils/customLogger";
import { AsteraOncologyExternalTableState } from "../hooks/useAsteraOncologyExternalTableData";
import { AsteraOncologyExternalFilterPayload } from "../hooks/useFilterPayload";
import { fetchAsteraOncologyExternalData } from "./fetchAsteraOncologyExternalData";

/**
 * makePostCallForAsteraOncologyExternal - API Call for Filter/Search Changes
 *
 * Purpose: Fetches data when filters or search term changes.
 * Resets tableData to new results and increments pagination reset count.
 *
 * @param payload - Filter payload containing search params
 * @param setAsteraOncologyExternalState - State setter for API response
 * @param setTableData - State setter for table data
 * @param setPaginationResetCount - Function to reset pagination
 */
export const makePostCallForAsteraOncologyExternal = async (
  payload: AsteraOncologyExternalFilterPayload,
  setAsteraOncologyExternalState: React.Dispatch<
    React.SetStateAction<AsteraOncologyExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
): Promise<void> => {
  try {
    setAsteraOncologyExternalState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    const response: OncologySubmissionApiResponse =
      await fetchAsteraOncologyExternalData(payload);

    if (response.success) {
      setAsteraOncologyExternalState({
        data: response,
        loading: false,
        error: null,
        showInlineLoader: false,
      });
      setTableData(response.rows);

      // Reset pagination to page 1 when filters/search change
      setPaginationResetCount((prev) => prev + 1);
    } else {
      setAsteraOncologyExternalState((prev) => ({
        ...prev,
        loading: false,
        error: response.message || "Failed to fetch data",
      }));
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    setAsteraOncologyExternalState((prev) => ({
      ...prev,
      loading: false,
      error: errorMessage,
    }));
    logError(error as Error, "Error in makePostCallForAsteraOncologyExternal");
  }
};
